// src/components/issues/tableau/BarreTableau.tsx — SCE Issues
// Au-dessus du tableau : recherche, vues prêtes à l'emploi, regroupement,
// colonnes, densité, puis ce qui est affiché (compteur) et les filtres actifs en
// pastilles. Au téléphone : recherche, vues, tri et filtres seulement — le
// reste (colonnes, densité) n'a pas de sens sur des cartes.

import { useState } from 'react'
import { ChevronDown, Columns3, Layers, Pin, Plus, Rows3, Search, SlidersHorizontal, AlignJustify, X } from 'lucide-react'
import type { ColonneId, CritereTri, Regroupement, ReglagesTableau, VuePredefinie } from '@/utils/tableauProblemes'
import {
  DEFINITIONS_COLONNES,
  LIBELLE_REGROUPEMENT,
  PLURIEL_REGROUPEMENT,
  nbFiltresActifs,
  triEffectif,
  triParDefaut,
} from '@/utils/tableauProblemes'
import { MenuColonnes } from './MenuColonnes'
import { MenuNouvelleVue } from './MenuNouvelleVue'
import { MenuRegroupement } from './MenuRegroupement'
import { texteValeur } from './valeurs'

export interface BarreTableauProps {
  estBureau: boolean
  interne: boolean
  recherche: string
  onRecherche: (texte: string) => void
  /** Vues fixes puis vues personnelles. */
  vues: readonly VuePredefinie[]
  /** Les vues fixes seules (un nom de vue perso ne doit pas les répéter). */
  vuesFixes: readonly VuePredefinie[]
  comptesVues: Readonly<Record<string, number>>
  vueActiveId: string | null
  onVue: (vue: VuePredefinie) => void
  onEnregistrerVue: (nom: string) => void
  onSupprimerVue: (vue: VuePredefinie) => void
  /** « Épingler à l'accueil » une vue personnelle (accueil personnalisable, lot 5) ; absent = pas proposé. */
  epingler?: { estEpinglee: (vue: VuePredefinie) => boolean; onEpingler: (vue: VuePredefinie) => void }
  reglages: ReglagesTableau
  modifier: (changer: (prev: ReglagesTableau) => ReglagesTableau) => void
  /** Combien sont affichés / combien comptent comme problèmes. */
  affiches: number
  /** Rangées affichées : moins que `affiches` quand des fiches sœurs en partagent une. */
  nbRangees: number
  total: number
  annulesMasques: number
  /** Nombre de groupes quand le tableau est regroupé. */
  nbGroupes: number
  toutEstReplie: boolean
  onToutReplier: () => void
  onOuvrirFiltre: (col: ColonneId, ancre: HTMLElement) => void
  onOuvrirFeuille: () => void
  onToutEffacer: () => void
}

type Menu = 'regroupement' | 'colonnes' | 'vue' | null

const LIBELLES_TRI_TEL: { valeur: string; libelle: string; tri: CritereTri[] }[] = [
  { valeur: 'recent', libelle: 'Plus récents d’abord', tri: [] },
  { valeur: 'ancien', libelle: 'Plus anciens d’abord', tri: [{ col: 'date', sens: 'asc' }] },
  { valeur: 'criticite', libelle: 'Criticité', tri: [{ col: 'criticite', sens: 'asc' }] },
  { valeur: 'machine', libelle: 'N° de série', tri: [{ col: 'machine', sens: 'asc' }] },
]

function texteTri(tri: readonly CritereTri[]): string {
  return tri.map((t) => `${DEFINITIONS_COLONNES[t.col].libelle} ${t.sens === 'asc' ? '↑' : '↓'}`).join(' puis ')
}

export function BarreTableau(p: BarreTableauProps): JSX.Element {
  const [menu, setMenu] = useState<{ nom: Exclude<Menu, null>; ancre: HTMLElement } | null>(null)
  const { reglages, modifier } = p
  const nbFiltres = nbFiltresActifs(reglages)

  function basculerMenu(nom: Exclude<Menu, null>, ancre: HTMLElement): void {
    setMenu((m) => (m?.nom === nom ? null : { nom, ancre }))
  }

  // ─── Pastilles : ce qui filtre en ce moment ───
  const pastilles = (Object.entries(reglages.filtres) as [ColonneId, string[]][])
    .filter(([, v]) => v.length > 0)
    .map(([col, valeurs]) => ({
      col,
      texte: `${DEFINITIONS_COLONNES[col].libelle} : ${
        valeurs.length <= 2 ? valeurs.map((v) => texteValeur(col, v)).join(', ') : `${valeurs.length} valeurs`
      }`,
    }))
  const triPerso = !triParDefaut(reglages)
  const aEffacer = nbFiltres > 1 || (nbFiltres > 0 && triPerso)

  const selectionTri =
    LIBELLES_TRI_TEL.find((o) => JSON.stringify(o.tri) === JSON.stringify(reglages.tri))?.valeur ?? 'recent'

  return (
    <div className="tableau-barre">
      <div className="tableau-outils">
        <label className="tableau-recherche">
          <Search size={17} aria-hidden="true" />
          <span className="sr-only">Rechercher un problème</span>
          <input
            type="search"
            value={p.recherche}
            onChange={(e) => p.onRecherche(e.target.value)}
            placeholder="Rechercher un problème, un équipement, un déclarant…"
            autoComplete="off"
          />
        </label>

        <div className="tableau-vues" role="group" aria-label="Vues">
          {p.vues.map((v) =>
            v.perso ? (
              <span key={v.id} className="tableau-vue tableau-vue--perso" data-actif={p.vueActiveId === v.id}>
                <button type="button" className="tableau-vue-choix" aria-pressed={p.vueActiveId === v.id} onClick={() => p.onVue(v)}>
                  {v.libelle}
                  <span className="tableau-vue-nombre">{p.comptesVues[v.id] ?? 0}</span>
                </button>
                {p.epingler && (
                  <button
                    type="button"
                    className="tableau-vue-retirer tableau-vue-epingler"
                    aria-label={p.epingler.estEpinglee(v) ? `La vue ${v.libelle} est une file de votre accueil` : `Épingler la vue ${v.libelle} à l’accueil`}
                    title={p.epingler.estEpinglee(v) ? 'Déjà une file de votre accueil' : 'Épingler à l’accueil, comme file'}
                    aria-pressed={p.epingler.estEpinglee(v)}
                    disabled={p.epingler.estEpinglee(v)}
                    onClick={() => p.epingler?.onEpingler(v)}
                  >
                    <Pin size={13} />
                  </button>
                )}
                <button
                  type="button"
                  className="tableau-vue-retirer"
                  aria-label={`Supprimer la vue ${v.libelle}`}
                  title="Supprimer cette vue"
                  onClick={() => p.onSupprimerVue(v)}
                >
                  <X size={13} />
                </button>
              </span>
            ) : (
              <button
                key={v.id}
                type="button"
                className="tableau-vue"
                aria-pressed={p.vueActiveId === v.id}
                onClick={() => p.onVue(v)}
              >
                {v.libelle}
                <span className="tableau-vue-nombre">{p.comptesVues[v.id] ?? 0}</span>
              </button>
            ),
          )}
          <button
            type="button"
            className="tableau-vue tableau-vue--ajout"
            aria-label="Enregistrer la vue en cours"
            title="Enregistrer les filtres, le tri et le regroupement en cours sous un nom"
            aria-haspopup="dialog"
            onClick={(e) => basculerMenu('vue', e.currentTarget)}
          >
            <Plus size={16} />
            {p.vues.every((v) => !v.perso) && p.estBureau && <span>Vue</span>}
          </button>
        </div>

        {!p.estBureau && (
          <>
            <select
              className="tableau-tri-tel"
              aria-label="Trier"
              value={selectionTri}
              onChange={(e) => {
                const o = LIBELLES_TRI_TEL.find((x) => x.valeur === e.target.value)
                if (o) modifier((r) => ({ ...r, tri: o.tri.map((t) => ({ ...t })) }))
              }}
            >
              {LIBELLES_TRI_TEL.map((o) => (
                <option key={o.valeur} value={o.valeur}>
                  {o.libelle}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={'tableau-bouton' + (nbFiltres > 0 ? ' tableau-bouton--actif' : '')}
              aria-label="Filtres"
              onClick={p.onOuvrirFeuille}
            >
              <SlidersHorizontal size={16} />
              {nbFiltres > 0 && <b>{nbFiltres}</b>}
            </button>
          </>
        )}

        {p.estBureau && (
          <div className="tableau-reglages">
            <button
              type="button"
              className={'tableau-bouton' + (reglages.regroupement ? ' tableau-bouton--actif' : '')}
              aria-haspopup="dialog"
              onClick={(e) => basculerMenu('regroupement', e.currentTarget)}
            >
              <Layers size={16} />
              {reglages.regroupement ? `Par ${LIBELLE_REGROUPEMENT[reglages.regroupement]}` : 'Regrouper'}
              <ChevronDown size={14} />
            </button>
            <button
              type="button"
              className="tableau-bouton"
              aria-haspopup="dialog"
              onClick={(e) => basculerMenu('colonnes', e.currentTarget)}
            >
              <Columns3 size={16} />
              Colonnes
              <b>
                {reglages.visibles.length}/{p.interne ? Object.keys(DEFINITIONS_COLONNES).length : Object.values(DEFINITIONS_COLONNES).filter((d) => !d.referentiel).length}
              </b>
            </button>
            <div className="tableau-densite" role="group" aria-label="Densité">
              <button
                type="button"
                aria-pressed={reglages.densite === 'compact'}
                aria-label="Compact"
                title="Compact"
                onClick={() => modifier((r) => ({ ...r, densite: 'compact' }))}
              >
                <AlignJustify size={16} />
              </button>
              <button
                type="button"
                aria-pressed={reglages.densite === 'confortable'}
                aria-label="Confortable"
                title="Confortable"
                onClick={() => modifier((r) => ({ ...r, densite: 'confortable' }))}
              >
                <Rows3 size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="tableau-meta">
        <span>
          <b>{p.affiches}</b> sur {p.total} problème{p.total > 1 ? 's' : ''}
          {p.nbRangees < p.affiches && (
            <>
              {' '}
              · {p.nbRangees} ligne{p.nbRangees > 1 ? 's' : ''}
            </>
          )}
          {p.estBureau && reglages.regroupement && (
            <>
              {' '}
              · {p.nbGroupes}{' '}
              {PLURIEL_REGROUPEMENT[reglages.regroupement][p.nbGroupes > 1 ? 1 : 0]}
            </>
          )}
          {p.annulesMasques > 0 && (
            <>
              {' '}
              · {p.annulesMasques} annulé{p.annulesMasques > 1 ? 's' : ''} masqué{p.annulesMasques > 1 ? 's' : ''} (cochez
              « Annulé » dans Statut)
            </>
          )}
        </span>
        {p.estBureau && reglages.regroupement && p.nbGroupes > 0 && (
          <button type="button" className="tableau-lien" onClick={p.onToutReplier}>
            {p.toutEstReplie ? 'Tout déplier' : 'Tout replier'}
          </button>
        )}
      </div>

      {p.estBureau && (pastilles.length > 0 || reglages.mesDeclarations || reglages.suivis || triPerso) && (
        <div className="tableau-pastilles">
          {reglages.suivis && (
            <span className="tableau-pastille">
              <span className="tableau-pastille-libelle">Suivis</span>
              <button
                type="button"
                aria-label="Retirer Suivis"
                onClick={() => modifier((r) => ({ ...r, suivis: false }))}
              >
                <X size={13} />
              </button>
            </span>
          )}
          {reglages.mesDeclarations && (
            <span className="tableau-pastille">
              <span className="tableau-pastille-libelle">Mes déclarations</span>
              <button
                type="button"
                aria-label="Retirer Mes déclarations"
                onClick={() => modifier((r) => ({ ...r, mesDeclarations: false }))}
              >
                <X size={13} />
              </button>
            </span>
          )}
          {pastilles.map((x) => (
            <span key={x.col} className="tableau-pastille">
              <button type="button" className="tableau-pastille-libelle" onClick={(e) => p.onOuvrirFiltre(x.col, e.currentTarget)}>
                {x.texte}
              </button>
              <button
                type="button"
                aria-label={`Retirer le filtre ${DEFINITIONS_COLONNES[x.col].libelle}`}
                onClick={() =>
                  modifier((r) => {
                    const { [x.col]: _retire, ...reste } = r.filtres
                    void _retire
                    return { ...r, filtres: reste }
                  })
                }
              >
                <X size={13} />
              </button>
            </span>
          ))}
          {triPerso && (
            <span className="tableau-pastille tableau-pastille--douce">
              <span className="tableau-pastille-libelle">Tri : {texteTri(triEffectif(reglages))}</span>
              <button type="button" aria-label="Revenir au tri par défaut" onClick={() => modifier((r) => ({ ...r, tri: [] }))}>
                <X size={13} />
              </button>
            </span>
          )}
          {aEffacer && (
            <button type="button" className="tableau-lien" onClick={p.onToutEffacer}>
              Tout effacer
            </button>
          )}
        </div>
      )}

      {menu?.nom === 'regroupement' && (
        <MenuRegroupement
          ancre={menu.ancre}
          valeur={reglages.regroupement}
          onChoisir={(mode: Regroupement | null) => modifier((r) => ({ ...r, regroupement: mode }))}
          onClose={() => setMenu(null)}
        />
      )}
      {menu?.nom === 'vue' && (
        <MenuNouvelleVue
          ancre={menu.ancre}
          reglages={reglages}
          vuesFixes={p.vuesFixes}
          onEnregistrer={p.onEnregistrerVue}
          onClose={() => setMenu(null)}
        />
      )}
      {menu?.nom === 'colonnes' && (
        <MenuColonnes
          ancre={menu.ancre}
          reglages={reglages}
          interne={p.interne}
          modifier={modifier}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  )
}
